/**
 * TanStack Query Mutation Patterns
 *
 * Higher-level mutation hooks with automatic cache invalidation.
 * Built on the existing ApiClient.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/mutations
 * @added DOC-0033 (2025-12-15)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '../api/client';
import { clearQueryResponseCache } from './query-helpers';

type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Options for creating a mutation with automatic invalidation.
 */
export interface CreateMutationOptions<TData> {
  /** API endpoint path */
  path: string;
  /** HTTP method */
  method: MutationMethod;
  /** Zod schema for response validation */
  schema: z.ZodType<TData>;
  /** Query keys to invalidate on success */
  invalidateKeys?: readonly QueryKey[];
}

/**
 * Create a mutation hook with automatic cache invalidation.
 *
 * Uses existing ApiClient for requests, preserving:
 * - Correlation IDs for debugging
 * - Zod schema validation
 * - Timeout handling
 *
 * @example
 * ```tsx
 * function CreateCourseButton() {
 *   const createCourse = useApiMutationQuery<CourseResponse, CreateCourseInput>({
 *     path: '/courses',
 *     method: 'POST',
 *     schema: courseSchema,
 *     invalidateKeys: [queryKeys.courses.all],
 *   });
 *
 *   const handleCreate = () => {
 *     createCourse.mutate({ name: 'New Course' });
 *   };
 *
 *   return (
 *     <button onClick={handleCreate} disabled={createCourse.isPending}>
 *       {createCourse.isPending ? 'Creating...' : 'Create Course'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useApiMutationQuery<TData, TVariables = void>(
  config: CreateMutationOptions<TData>
) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables: TVariables): Promise<TData> => {
      const requestOpts = { schema: config.schema };

      switch (config.method) {
        case 'POST':
          return api.post<TData, TVariables>(config.path, variables, requestOpts);
        case 'PUT':
          return api.put<TData, TVariables>(config.path, variables, requestOpts);
        case 'PATCH':
          return api.patch<TData, TVariables>(config.path, variables, requestOpts);
        case 'DELETE':
          return api.delete<TData>(config.path, requestOpts);
      }
    },
    onSuccess: () => {
      clearQueryResponseCache();

      // Invalidate specified query keys
      if (config.invalidateKeys) {
        for (const key of config.invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}

/**
 * Create a DELETE mutation for resource deletion by ID.
 *
 * @example
 * ```tsx
 * const deleteCourse = useDeleteMutation<SuccessResponse>({
 *   basePath: '/courses',
 *   schema: successSchema,
 *   invalidateKeys: [queryKeys.courses.all],
 * });
 *
 * deleteCourse.mutate('course-id-123');
 * ```
 */
export function useDeleteMutation<TData>(config: {
  /** Base path - ID will be appended */
  basePath: string;
  /** Zod schema for response validation */
  schema: z.ZodType<TData>;
  /** Query keys to invalidate on success */
  invalidateKeys?: readonly QueryKey[];
}) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, string>({
    mutationFn: async (id: string): Promise<TData> => {
      return api.delete<TData>(`${config.basePath}/${id}`, {
        schema: config.schema,
      });
    },
    onSuccess: () => {
      clearQueryResponseCache();

      if (config.invalidateKeys) {
        for (const key of config.invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
